import { getIntegrationData } from '@/modules/integration/services/integrationDataUtilities'
import { customFieldKey } from '@/modules/integration/services/integrationUtilities'
import {
  autoSyncLogicDefinitionForSegment,
  isAQuestion,
  renderText,
  segmentGroupSyncSetupCompletion,
  segmentName
} from '@/modules/segment/services/segmentUtilities'
import { stripHtml } from '@/services/string'
import type { ConditionOperator, ConditionSourceType, ConditionValueType } from '@/components/common/liquidConditionals'

export type EmailPersonalizationField = {
  id: string
  sourceType: ConditionSourceType
  label: string
  token: string
  defaultOperator: ConditionOperator
  operators: ConditionOperator[]
  valueType: ConditionValueType
  values?: Array<{ label: string; value: string }>
}

type CatalogOptions = {
  integratedPartner: any
  segmentGroups: any[]
  questionForSegmentGroupId?: (segmentGroupId: string) => any
}

const DEFAULT_OPERATORS: ConditionOperator[] = ['==', '!=', 'contains', '>', '<', '>=', '<=']
const TEXT_OPERATORS: ConditionOperator[] = ['==', '!=', 'contains']
const BOOLEAN_OPERATORS: ConditionOperator[] = ['==', '!=']
const TAG_OPERATORS: ConditionOperator[] = ['contains']

export async function buildEmailPersonalizationFieldCatalog({
  integratedPartner,
  segmentGroups,
  questionForSegmentGroupId
}: CatalogOptions): Promise<EmailPersonalizationField[]> {
  const fields: EmailPersonalizationField[] = []
  const liquidVariable = partnerLiquidVariable(integratedPartner)

  fields.push(
    ...(await buildSegmentFields(segmentGroups, questionForSegmentGroupId, liquidVariable, integratedPartner))
  )
  fields.push(...(await buildNativeFields(liquidVariable)))
  fields.push(...(await buildCustomFields(liquidVariable)))
  fields.push(...(await buildTagFields(liquidVariable, integratedPartner)))

  const seen = new Set<string>()
  return fields.filter((field) => {
    const key = `${field.sourceType}:${field.token}:${field.id}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function partnerLiquidVariable(integratedPartner: any): string | null {
  if (integratedPartner && Object.prototype.hasOwnProperty.call(integratedPartner, 'liquidVariable')) {
    return integratedPartner.liquidVariable || null
  }
  return 'subscriber'
}

function tokenFor(liquidVariable: string | null, fieldToken: string): string {
  if (!liquidVariable) return fieldToken
  if (fieldToken.includes('.')) return fieldToken
  return `${liquidVariable}.${fieldToken}`
}

function operatorForPartner(integratedPartner: any): ConditionOperator {
  return integratedPartner?.customFieldComparisonOperator || integratedPartner?.operator || '=='
}

function valueTypeForDataType(dataType?: string): ConditionValueType {
  const normalized = String(dataType || '').toLowerCase()
  if (['number', 'integer', 'float', 'decimal'].includes(normalized)) return 'number'
  if (['boolean', 'bool'].includes(normalized)) return 'boolean'
  return 'string'
}

function operatorsForDataType(dataType?: string): ConditionOperator[] {
  const valueType = valueTypeForDataType(dataType)
  if (valueType === 'number') return DEFAULT_OPERATORS
  if (valueType === 'boolean') return BOOLEAN_OPERATORS
  return TEXT_OPERATORS
}

async function buildSegmentFields(
  segmentGroups: any[],
  questionForSegmentGroupId: ((segmentGroupId: string) => any) | undefined,
  liquidVariable: string | null,
  integratedPartner: any
): Promise<EmailPersonalizationField[]> {
  return Promise.all(
    segmentGroups
      .filter((segmentGroup) => {
        return (
          isAQuestion(segmentGroup) &&
          segmentGroup.syncToCustomField &&
          (segmentGroupSyncSetupCompletion(segmentGroup) === 'some_values' ||
            segmentGroupSyncSetupCompletion(segmentGroup) === 'all_values')
        )
      })
      .map(async (segmentGroup) => {
        const question = questionForSegmentGroupId?.(segmentGroup.id)
        const label = stripHtml(renderText(question?.text || segmentGroup.name || 'Question', 1000))
        const rawField = String(segmentGroup.syncToCustomField)
        const fieldToken = await customFieldKey(rawField)
        const values = segmentGroup.segments
          .map((segment: any) => {
            const syncValue = autoSyncLogicDefinitionForSegment(segment)?.value
            if (!syncValue) return null
            return {
              label: stripHtml(segmentName(segment)),
              value: String(syncValue)
            }
          })
          .filter(Boolean) as Array<{ label: string; value: string }>

        return {
          id: `segment:${segmentGroup.id}`,
          sourceType: 'segment' as const,
          label,
          token: tokenFor(liquidVariable, fieldToken),
          defaultOperator: operatorForPartner(integratedPartner),
          operators: TEXT_OPERATORS,
          valueType: 'string' as const,
          values
        }
      })
  ).then((fields) => fields.filter((field) => field.token))
}

async function buildNativeFields(liquidVariable: string | null): Promise<EmailPersonalizationField[]> {
  try {
    const nativeFields = await getIntegrationData('partner', 'native_fields')
    return nativeFields.value
      .map((field: any) => {
        const fieldToken = String(field.templatingToken || field.key || field.id || field.name || '')
        const valueType = valueTypeForDataType(field.dataType || field.type)
        return {
          id: `native_field:${field.id || fieldToken}`,
          sourceType: 'native_field' as const,
          label: field.label || field.name || fieldToken,
          token: tokenFor(liquidVariable, fieldToken),
          defaultOperator: '==' as const,
          operators: operatorsForDataType(field.dataType || field.type),
          valueType
        }
      })
      .filter((field: EmailPersonalizationField) => field.token)
  } catch {
    return []
  }
}

async function buildCustomFields(liquidVariable: string | null): Promise<EmailPersonalizationField[]> {
  try {
    const customFields = await getIntegrationData('partner', 'custom_fields')
    return await Promise.all(
      customFields.value.map(async (field: any) => {
        const fieldToken = await customFieldKey(String(field.id || field.key || field.templatingToken || ''))
        const valueType = valueTypeForDataType(field.dataType || field.type)
        return {
          id: `custom_field:${field.id || fieldToken}`,
          sourceType: 'custom_field' as const,
          label: field.label || field.name || fieldToken,
          token: tokenFor(liquidVariable, fieldToken),
          defaultOperator: '==' as const,
          operators: operatorsForDataType(field.dataType || field.type),
          valueType
        }
      })
    ).then((fields) => fields.filter((field) => field.token))
  } catch {
    return []
  }
}

async function buildTagFields(
  liquidVariable: string | null,
  integratedPartner: any
): Promise<EmailPersonalizationField[]> {
  if (!integratedPartner?.espFeatureSupport?.tag) return []

  try {
    const tags = await getIntegrationData('partner', 'tags')
    const values = tags.value.map((tag: any) => {
      const label = tag.label || tag.name || tag.id
      return { label, value: String(label) }
    })

    if (!values.length) return []

    return [
      {
        id: 'tag:tags',
        sourceType: 'tag' as const,
        label: 'Tags',
        token: tokenFor(liquidVariable, 'tags'),
        defaultOperator: 'contains' as const,
        operators: TAG_OPERATORS,
        valueType: 'string' as const,
        values
      }
    ].filter((field: EmailPersonalizationField) => field.token)
  } catch {
    return []
  }
}
